import { type ColorScheme, NavigationPosition } from '@unifyapps/defs/types/interface';
import SvgChevronDown from '@unifyapps/icons/outline/ChevronDown';
import SvgLogOut01 from '@unifyapps/icons/outline/LogOut01';
import { Avatar } from '@unifyapps/ui/components/Avatar';
import { Box } from '@unifyapps/ui/components/Box';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Popover } from '@unifyapps/ui/components/Popover';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { useThemeOverrideContext } from '@unifyapps/ui/providers/ThemeOverrideContext';
import { clsx } from 'clsx';
import { useCallback } from 'react';
import { EMPTY_OBJECT } from '../../../../consts/empty';
import { isHorizontalUpfrontFn, isPlatformSideNav } from '../../helpers';
import { userNavItemVariants } from '../../styles';
import type { NavigationItemSlotPropsType, NavUserMenu } from '../../types';

const CHEVRON_STYLES = { transform: 'rotate(-90deg)' };

const DARK_BORDER_STYLE = {
  '--border-tertiary': 'var(--utility-gray-100)',
} as React.CSSProperties;

// dark/platform rail avatar — authored Figma: translucent-white vertical gradient + white initials
// (the old brand-tinted dark style read as a broken pale blob on the deep-green rail).
const DARK_AVATAR_STYLE = {
  '--avatar-contrast-border': 'transparent',
  background: 'var(--palette-gray-300)',
  color: 'var(--palette-gray-600)',
} as React.CSSProperties;

/** Used in matrix */
const DARK_MODE_STYLE = {
  '--avatar-contrast-border': 'var(--utility-brand-500)',
} as React.CSSProperties;

function getAvatarRootStyle(isPlatformRail: boolean, scheme?: ColorScheme): React.CSSProperties {
  if (isPlatformRail) return DARK_AVATAR_STYLE;
  if (scheme === 'dark') return DARK_MODE_STYLE;
  return EMPTY_OBJECT;
}

const POPOVER_OFFSET = {
  mainAxis: 8,
  crossAxis: 1,
};

// platform user menu is fixed at 276px in both rail states. when expanded (292px
// rail), the centered `top` placement + crossAxis 0 yields an 8px inset each side —
// (292 - 276) / 2 = 8.
const PLATFORM_POPOVER_WIDTH = 17.25; // 276px
const PLATFORM_EXPANDED_POPOVER_OFFSET = {
  mainAxis: 4,
  crossAxis: 0,
};

const USER_NAV_ITEM = 'user-nav-item';

// TODO : Expose this in Configuration
function HorizontalUpfrontUserNavigationItem({ user }: { user: NavUserMenu['user'] }) {
  return (
    <Box className="gap-sm flex h-9 w-full items-center justify-between">
      <Box className="gap-md flex items-center">
        <Avatar color="brand_subtle" name={user.name} size="sm" />
        <Typography
          as="span"
          className="ua-side-nav-item-active"
          color="text-primary"
          maxNumberOfLines={1}
          variant="text-sm"
          weight="semi-bold"
        >
          {user.name}
        </Typography>
      </Box>
      <Box className="flex-shrink-0 cursor-pointer">
        <SvgLogOut01 className="ua-side-nav-item-active-false size-5" />
      </Box>
    </Box>
  );
}

export function UserNavigationItem({
  navItem,
  open,
  onOpenChange: onOpenChangeProp,
  slotProps,
  activePathnames,
}: {
  navItem: NavUserMenu;
  open: boolean;
  onOpenChange: (id: string, open: boolean) => void;
  slotProps?: NavigationItemSlotPropsType;
  activePathnames: string[];
}) {
  const { scheme } = useThemeOverrideContext();
  const { id, user, customerName, MenuList, disabled = false, onClick } = navItem;
  const { expanded, labelVariant, selectionVariant, direction, position } =
    slotProps?.navigationItem ?? (EMPTY_OBJECT as NavigationItemSlotPropsType['navigationItem']);

  const isHorizontalUpfront = isHorizontalUpfrontFn(labelVariant, selectionVariant, direction);

  const borderColorClassName = slotProps?.borderColorClassName;

  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      // onClick used to close the notifications drawer
      onClick?.();
      onOpenChangeProp(id, nextOpen);
    },
    [id, onOpenChangeProp, onClick],
  );

  const {
    ignoreBoundary,
    minWidth,
    maxWidth,
    placement,
    className: popupContentClassName,
    transform,
  } = navItem.popupProps ?? {};

  const _isPlatformSideNav = isPlatformSideNav(labelVariant);

  // expanded platform rail anchors the menu above the user item at a fixed inset;
  // collapsed rail keeps the sideways (right-end) popup from popupProps.
  const isPlatformExpanded = _isPlatformSideNav && expanded;
  const effectivePlacement = isPlatformExpanded ? 'top' : placement;
  // platform rail pins the menu to 276px in both states; only the expanded case
  // reanchors above the item (placement/offset/margin below).
  const effectiveWidth = _isPlatformSideNav ? PLATFORM_POPOVER_WIDTH : undefined;
  const effectiveOffset = isPlatformExpanded ? PLATFORM_EXPANDED_POPOVER_OFFSET : POPOVER_OFFSET;

  return (
    <Popover onOpenChange={onOpenChange} open={open}>
      {({ onClose }) => (
        <>
          <Popover.Button
            className={clsx('cursor-pointer', { 'pointer-events-none': disabled })}
            {...(_isPlatformSideNav ? { stopPropagation: true } : {})}
          >
            <Stack
              className={clsx(
                'gap-x-md p-lg items-center',
                userNavItemVariants({
                  active: open,
                  labelVariant,
                  selectionVariant,
                  isHorizontalUpfront,
                  position,
                }),
                borderColorClassName,
                USER_NAV_ITEM,
                { 'group/PlatformSideNavUserSwitcher': _isPlatformSideNav },
                // TODO: remove this once get the complete Design for top Navigation position
                position !== NavigationPosition.Top
                  ? 'border-t border-[var(--navbar-border)] is-platform:border-primary'
                  : '',
                { 'ps-lg': _isPlatformSideNav },
              )}
              direction="row"
              style={scheme === 'dark' ? DARK_BORDER_STYLE : {}}
            >
              {isHorizontalUpfront ? (
                <HorizontalUpfrontUserNavigationItem user={user} />
              ) : (
                <Box className="flex h-9 items-center justify-center">
                  <Avatar
                    className={clsx({
                      'bg-utility-gray-300 text-tertiary': _isPlatformSideNav,
                    })}
                    color="brand_subtle"
                    name={user.name}
                    size="sm"
                    slotProps={{
                      root: { style: getAvatarRootStyle(_isPlatformSideNav, scheme) },
                    }}
                  />
                </Box>
              )}
              {expanded ? (
                <>
                  <Stack className={clsx('flex-1 truncate transition-all')}>
                    {/* platform green rail: text-primary/text-tertiary are dark here, so use the
                        authored cream avatar tokens (className wins over the color prop via merge). */}
                    <Typography
                      className={clsx({
                        'text-[color:var(--nav-user-name-fg)]': _isPlatformSideNav,
                      })}
                      color="text-primary"
                      variant="text-sm"
                      weight={_isPlatformSideNav ? 'medium' : 'semi-bold'}
                    >
                      {user.name}
                    </Typography>
                    <Typography
                      className={clsx({
                        'text-[color:var(--nav-user-role-fg)]': _isPlatformSideNav,
                      })}
                      color="text-tertiary"
                      variant="text-xs"
                      weight="regular"
                    >
                      {customerName}
                    </Typography>
                  </Stack>
                  {/* render as a span (component="span"), not the default <button>: this sits inside
                      Popover.Button which is already a <button>, so a nested button would be invalid
                      markup. The row owns the click — the IconButton only supplies hover/press chrome. */}
                  <IconButton
                    aria-hidden
                    className={clsx('flex-shrink-0', {
                      'opacity-0 transition-opacity group-hover/PlatformSideNavUserSwitcher:opacity-100':
                        _isPlatformSideNav,
                    })}
                    component="span"
                    Icon={SvgChevronDown}
                    iconClassName={clsx('size-4', { 'text-icon-primary': _isPlatformSideNav })}
                    size="sm"
                    style={CHEVRON_STYLES}
                    variant="ghost"
                  />
                </>
              ) : null}
            </Stack>
          </Popover.Button>
          <Popover.Content
            className={clsx('!rounded-lg', popupContentClassName, {
              // opening above the item: the sideways `mb-md` becomes an unwanted top gap
              '!mb-0': isPlatformExpanded,
            })}
            ignoreBoundary={ignoreBoundary}
            maxWidth={effectiveWidth ?? maxWidth}
            minWidth={effectiveWidth ?? minWidth}
            offset={effectiveOffset}
            placement={effectivePlacement}
            popupClassName="z-navbar"
            transform={transform}
            useAnchorAsContainer
          >
            {MenuList ? <MenuList activePathnames={activePathnames} onClose={onClose} /> : null}
          </Popover.Content>
        </>
      )}
    </Popover>
  );
}
